import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import type { Account } from '../api/client';
import { useIsNarrow } from '../hooks/useIsNarrow';

interface SidebarContextType {
  selectedAccount: Account | null;
  setSelectedAccount: (account: Account | null) => void;
  /** The menu column (Dashboard, Agency, Tools) is open beside the account rail. */
  isNavOpen: boolean;
  toggleNav: () => void;
  /** The account rail shows names, search and filters, not just avatars. */
  isRailExpanded: boolean;
  toggleRail: () => void;
  /** Both columns together, in px; 0 on phones, where the sidebar is a drawer. */
  sidebarWidth: number;
}

/** Sidebar (handoff 15, "two columns" 2a): an account rail, 76px or 264px wide,
 *  and the menu column beside it, 240px or closed. The main area starts after
 *  both; anything pinned to its left edge offsets by `sidebarWidth`, set inline
 *  on that element. Not a custom property on <html>: changing one restyles
 *  every element on the page, which cost a frame on each toggle. */
export const RAIL_WIDTH = 76;
export const RAIL_WIDTH_EXPANDED = 264;
export const NAV_WIDTH = 240;
/** Both columns' width transition (Layout.css); what slides beside the sidebar
 *  (useFoldSlide) follows it on the same timing. */
export const SIDEBAR_FOLD_MS = 260;
export const SIDEBAR_FOLD_EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

const NAV_OPEN_KEY = 'om_sb_nav_open';
const RAIL_EXPANDED_KEY = 'om_sb_rail_expanded';
/** The one-column sidebar's fold; seeds the menu column for people who folded it. */
const LEGACY_FOLDED_KEY = 'leftSidebarCollapsed';

function readFlag(key: string, fallback: boolean): boolean {
  try {
    const saved = localStorage.getItem(key);
    if (saved === 'true') return true;
    if (saved === 'false') return false;
    if (key === NAV_OPEN_KEY) return localStorage.getItem(LEGACY_FOLDED_KEY) !== 'true';
  } catch {
    /* blocked storage: defaults */
  }
  return fallback;
}
function writeFlag(key: string, value: boolean) {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    /* blocked storage: the choice just doesn't outlive the tab */
  }
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);
/** The setter alone, which never changes: pages that only report the open account
 *  (AccountView, ModelDataEditor) must not re-render when the sidebar folds. */
const SetSelectedAccountContext = createContext<((account: Account | null) => void) | undefined>(undefined);

/** Phones (below 900px): the menu drawer and the bottom tab bar that opens it.
 *  A page with its own bottom tab bar (the CRM tabs) claims the bottom edge; the
 *  shell then hides its bar and that page's bar carries the Menu tab instead. */
interface MobileMenuContextType {
  isMobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean | ((open: boolean) => boolean)) => void;
  bottomBarClaimed: boolean;
  claimBottomBar: () => () => void;
}
const MobileMenuContext = createContext<MobileMenuContextType | undefined>(undefined);

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [isNavOpen, setNavOpen] = useState(() => readFlag(NAV_OPEN_KEY, true));
  const [isRailExpanded, setRailExpanded] = useState(() => readFlag(RAIL_EXPANDED_KEY, false));
  const isNarrow = useIsNarrow();

  const toggleNav = useCallback(() => {
    setNavOpen((prev) => {
      writeFlag(NAV_OPEN_KEY, !prev);
      return !prev;
    });
  }, []);
  const toggleRail = useCallback(() => {
    setRailExpanded((prev) => {
      writeFlag(RAIL_EXPANDED_KEY, !prev);
      return !prev;
    });
  }, []);

  const sidebarWidth = isNarrow ? 0 : (isRailExpanded ? RAIL_WIDTH_EXPANDED : RAIL_WIDTH) + (isNavOpen ? NAV_WIDTH : 0);

  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bottomBarClaims, setBottomBarClaims] = useState(0);
  const claimBottomBar = useCallback(() => {
    setBottomBarClaims((n) => n + 1);
    return () => setBottomBarClaims((n) => n - 1);
  }, []);
  const mobileMenu = useMemo<MobileMenuContextType>(
    () => ({ isMobileMenuOpen, setMobileMenuOpen, bottomBarClaimed: bottomBarClaims > 0, claimBottomBar }),
    [isMobileMenuOpen, bottomBarClaims, claimBottomBar],
  );

  const value = useMemo<SidebarContextType>(
    () => ({ selectedAccount, setSelectedAccount, isNavOpen, toggleNav, isRailExpanded, toggleRail, sidebarWidth }),
    [selectedAccount, isNavOpen, toggleNav, isRailExpanded, toggleRail, sidebarWidth],
  );

  return (
    <SetSelectedAccountContext.Provider value={setSelectedAccount}>
      <SidebarContext.Provider value={value}>
        <MobileMenuContext.Provider value={mobileMenu}>{children}</MobileMenuContext.Provider>
      </SidebarContext.Provider>
    </SetSelectedAccountContext.Provider>
  );
}

export function useSetSelectedAccount() {
  const context = useContext(SetSelectedAccountContext);
  if (context === undefined) {
    throw new Error('useSetSelectedAccount must be used within a SidebarProvider');
  }
  return context;
}

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}

export function useMobileMenu() {
  const context = useContext(MobileMenuContext);
  if (context === undefined) {
    throw new Error('useMobileMenu must be used within a SidebarProvider');
  }
  return context;
}

/** For a page that draws its own bottom tab bar while mounted (see MobileMenuContext).
 *  Returns the menu, or null outside the shell (a page rendered on its own). */
export function useClaimBottomBar() {
  const context = useContext(MobileMenuContext);
  const claim = context?.claimBottomBar;
  // Before paint, so the shell bar never flashes under the page's own.
  useLayoutEffect(() => claim?.(), [claim]);
  return context ?? null;
}

/** Renders nothing; while mounted the shell hides its phone tab bar. */
export function ClaimBottomBar() {
  useClaimBottomBar();
  return null;
}
